import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import './Store.css';

const Store = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasPriceAccess, setHasPriceAccess] = useState(false);
  const { customer } = useCustomerAuth();

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchProductsByCategory(selectedCategory);
    } else {
      fetchProducts();
    }
  }, [selectedCategory, currentPage]);

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/store/categories');
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('customerToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`/api/store/products?page=${currentPage}&limit=12`, { headers });
      const data = await response.json();
      setProducts(data.products);
      setTotalPages(data.pagination.pages);
      setHasPriceAccess(data.hasPriceAccess || false);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  const fetchProductsByCategory = async (categoryId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('customerToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`/api/store/categories/${categoryId}/products?page=${currentPage}&limit=12`, { headers });
      const data = await response.json();
      setProducts(data.products);
      setTotalPages(data.pagination.pages);
      setHasPriceAccess(data.hasPriceAccess || false);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching category products:', error);
      setLoading(false);
    }
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSelectedCategory(null);
    setCurrentPage(1);
    fetchProducts();
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  return (
    <div className="store-container">
      <div className="store-header">
        <h1>Productos</h1>
        <p>Descubre nuestra amplia gama de productos y soluciones tecnológicas</p>
      </div>

      <div className="store-content">
        {/* Filters */}
        <div className="store-filters">
          {selectedCategory && (
            <button onClick={handleClearFilters} className="clear-filters">
              Mostrar todos los productos
            </button>
          )}
        </div>

        {/* Categories */}
        <div className="categories-section">
          <h2>Categorías</h2>
          <div className="categories-grid">
            <button
              className={`category-card ${!selectedCategory ? 'active' : ''}`}
              onClick={() => handleCategoryClick(null)}
            >
              <span>Todos los productos</span>
            </button>
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/tienda/categoria/${category.id}`}
                className="category-card"
              >
                <span>{category.name}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Products */}
        <div className="products-section">
          {loading ? (
            <div className="loading">Cargando productos...</div>
          ) : products.length === 0 ? (
            <div className="no-products">
              <p>No se encontraron productos.</p>
            </div>
          ) : (
            <>
              <div className="products-grid">
                {products.map((product) => (
                  <div key={product.id} className="product-card">
                    <div className="product-image">
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={product.images[0].image_url}
                          alt={product.short_title}
                          onError={(e) => {
                            e.target.src = '/assets/images/Distracker.png';
                          }}
                        />
                      ) : (
                        <img src="/assets/images/Distracker.png" alt="Default" />
                      )}
                    </div>
                    <div className="store-product-info">
                      <h3 className="product-title">{product.short_title}</h3>
                      <p className="product-long-title">{product.long_title}</p>
                      {/* <p className="product-category">{product.category_name}</p> */}
                      {hasPriceAccess && product.price !== null && (
                        <p className="product-price">{formatPrice(product.price)}</p>
                      )}
                      <Link to={`/tienda/producto/${product.id}`} className="view-product-btn">
                        Ver producto
                      </Link>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    Anterior
                  </button>

                  <span className="pagination-info">
                    Página {currentPage} de {totalPages}
                  </span>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Store;
